import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Public marketing landing at `/planner`. Built to match the design comp
 * pixel-for-pixel: warm cream backdrop, watercolour botanical accents, a
 * laptop+phone product mockup, three feature columns, three difference
 * columns, and a soft candle CTA at the bottom.
 *
 * Signed-in visitors still get a compact "You're already in — open your
 * Planner" chip in the top-right so the marketing screen doesn't block
 * them from getting to /planner/home.
 */
export default function PlannerLanding() {
  const { user } = useAuth();
  const signup = user ? "/planner/home" : "/signup?returnTo=/planner/home";
  return (
    <div
      className="min-h-screen bg-[#F5EFE4] text-[#2A2823] overflow-x-hidden"
      data-testid="planner-landing"
      style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      {/* Signed-in escape hatch */}
      {user && (
        <div className="absolute top-4 right-4 z-30" data-testid="planner-landing-open-app">
          <Link
            to="/planner/home"
            className="text-xs bg-white/80 hover:bg-white text-[#3E4A37] rounded-full px-4 h-9 inline-flex items-center gap-2 border border-[#A87F56]/30 shadow-sm"
          >
            Open your Planner &rarr;
          </Link>
        </div>
      )}

      {/* ---------------- HERO ---------------- */}
      <section className="relative pt-14 pb-24 sm:pb-32 lg:pb-40">
        {/* Watercolour branch — top right. Softer on mobile so it doesn't
            crowd the wordmark and its tagline. */}
        <img
          src="/willow-branch.png"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="pointer-events-none select-none absolute -top-2 -right-24 w-[240px] sm:w-[420px] lg:w-[600px] opacity-70 sm:opacity-90"
        />
        {/* Bottom-left basket peek — small partial photo tucked in the
            corner. Mostly cropped by the section edge; only the top of the
            basket is visible, with a soft fade on the top edge blending
            into the cream. Not the same instance as section 2's basket —
            this is a hero-only accent. */}
        <img
          src="/img/planner-basket.jpg"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="pointer-events-none select-none absolute left-0 -bottom-8 w-[160px] sm:w-[240px] lg:w-[300px]"
          style={{
            maskImage: "linear-gradient(to bottom, transparent 0%, black 45%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 45%)",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT column */}
          <div className="relative z-10 pt-6">
            <img
              src="/willow-wordmark.png"
              alt="Willoah"
              className="max-w-[240px] sm:max-w-[300px] w-full h-auto"
              draggable={false}
            />
            <div className="mt-1 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] text-[#A87F56] pl-2 whitespace-nowrap">
              JUST WHAT ACTUALLY WORKS
            </div>

            <h1
              className="mt-10 text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.05] text-[#2A2823] tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Finally, a planner<br />that holds your<br />whole&nbsp;week.
            </h1>

            <p className="mt-6 text-lg text-[#2A2823]/75 max-w-md leading-relaxed">
              Real recipes. Smart planning.<br />Less stress. More life.
            </p>

            <div className="mt-8">
              <Link
                to={signup}
                data-testid="planner-landing-cta-primary"
                className="inline-flex items-center justify-center rounded-full bg-[#3E4A37] hover:bg-[#2f3a2a] text-[#F7F3ED] px-8 h-14 text-[17px] font-medium shadow-[0_10px_30px_rgb(62,74,55,0.25)] transition"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Create your free account
              </Link>
              <p className="mt-3 text-sm text-[#2A2823]/55">
                No credit card. No app to download. Just relief.
              </p>
            </div>
          </div>

          {/* RIGHT column — real product mockup extracted from the design comp
              (laptop + phone photographed together with matching greenery).
              Static image for pixel fidelity; refresh via /public/img when the
              app UI changes materially. */}
          {/* RIGHT column — real product screenshot (transparent PNG, landscape).
              Aspect locked to the source so the browser can never squish it. */}
          <div className="relative">
            <img
              src="/img/planner-hero-mockup.png"
              alt="Willoah Planner shown on a laptop and phone — 'Hi, Kelly.' dashboard with Today, This Week, Meal Plan, and Goals & Home"
              data-testid="planner-landing-hero-mockup"
              draggable={false}
              className="block w-full h-auto max-w-none lg:min-w-[560px] mx-auto"
              style={{ aspectRatio: "500 / 420" }}
            />
          </div>
        </div>
      </section>

      {/* ---------------- "You don't need another app…" ---------------- */}
      <section className="relative bg-[#F1EBDF] py-16 sm:py-20 lg:py-28 overflow-hidden">
        {/* Section 2 basket — mobile: sits above the copy as a full-width
            banner; desktop: fills the left column top-to-bottom next to
            the text. Separate instance from the hero's small peek accent. */}
        <img
          src="/img/planner-basket.jpg"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="block md:hidden w-full h-56 object-cover object-center mb-8"
        />
        <img
          src="/img/planner-basket.jpg"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="pointer-events-none absolute left-0 top-0 h-full w-[300px] sm:w-[360px] lg:w-[420px] object-cover object-center hidden md:block"
        />
        {/* Right-side sprig — hidden on mobile to avoid crowding the copy */}
        <BotanicalSVG className="hidden lg:block absolute right-4 lg:right-16 top-1/2 -translate-y-1/2 w-[180px] opacity-70" />

        <div className="relative max-w-3xl mx-auto px-6 lg:pl-56 text-center lg:text-left">
          <h2
            className="text-[30px] sm:text-[36px] lg:text-[42px] leading-[1.15] text-[#2A2823]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            You don't need another app telling you to &ldquo;batch cook on&nbsp;Sundays.&rdquo;
          </h2>
          <p className="mt-6 text-[17px] leading-relaxed text-[#2A2823]/75 max-w-xl">
            You need someone to remember that Tuesday is a soccer night, that dinner still has to happen, and that you are one (1) person.
          </p>
        </div>
      </section>

      {/* ---------------- What it actually does ---------------- */}
      <section className="py-20 lg:py-24 bg-[#F5EFE4]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <h2
              className="text-[30px] sm:text-[36px] lg:text-[40px] text-[#2A2823]"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              What it actually does
            </h2>
            <div className="mt-3 flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-[#3E4A37]/40" />
              <SmallSprig />
              <div className="h-px w-16 bg-[#3E4A37]/40" />
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-10 lg:gap-14">
            <FeatureCol
              icon={<BowlIcon />}
              title={<>Real recipes,<br />not roulette.</>}
              body="Every recipe comes from someone who actually made it — not a stock photo and a stranger's guess."
            />
            <FeatureCol
              icon={<ClipboardIcon />}
              title={<>A shopping list<br />that builds itself.</>}
              body="Pick your meals. Get your list. No retyping what you already know."
            />
            <FeatureCol
              icon={<PeopleIcon />}
              title={<>You're not the only one<br />who knows the plan.</>}
              body={"Share your week with your family — so \u201Cwhat's for dinner\u201D stops being a question only you can answer."}
            />
          </div>
        </div>
      </section>

      {/* ---------------- The Willoah difference ---------------- */}
      <section className="relative py-20 lg:py-24 bg-[#EFE6D5] overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <h2
              className="text-[28px] sm:text-[34px] lg:text-[38px] text-[#2A2823] flex items-center justify-center flex-wrap gap-x-3"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <span>The</span>
              <img
                src="/willow-wordmark.png"
                alt="Willoah"
                data-testid="planner-landing-brand-mark-difference"
                draggable={false}
                className="inline-block h-14 sm:h-16 lg:h-20 w-auto -my-3"
              />
              <span>difference</span>
            </h2>
            <div className="mt-3 flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-[#3E4A37]/40" />
              <SmallSprig />
              <div className="h-px w-16 bg-[#3E4A37]/40" />
            </div>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-[#A87F56]/25">
            <DifferenceCol
              title={<>No algorithm<br />deciding what you see.</>}
            />
            <DifferenceCol
              title={<>No ten-dollar-a-month<br />&ldquo;premium&rdquo; wall between<br />you and your own calendar.</>}
            />
            <DifferenceCol
              title={<>Just your week,<br />actually held.</>}
            />
          </div>
        </div>
      </section>

      {/* ---------------- Final CTA — candle photo ---------------- */}
      <section
        data-testid="planner-landing-final-cta"
        className="relative min-h-[520px] lg:min-h-[600px] flex items-center justify-center"
        style={{
          backgroundImage: "linear-gradient(rgba(247,243,237,0.35), rgba(247,243,237,0.35)), url('/quote-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative max-w-3xl mx-auto px-6 text-center py-24">
          <h2
            className="text-[32px] sm:text-[40px] lg:text-[46px] leading-[1.15] text-[#2A2823]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            You don't have to carry it all.<br />
            Just the next right thing.
          </h2>
          <div className="mt-8">
            <Link
              to={signup}
              data-testid="planner-landing-cta-final"
              className="inline-flex items-center justify-center rounded-full bg-[#3E4A37] hover:bg-[#2f3a2a] text-[#F7F3ED] px-8 h-14 text-[17px] font-medium shadow-[0_10px_30px_rgb(62,74,55,0.25)] transition"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Create your free account
            </Link>
            <p className="mt-3 text-sm text-[#2A2823]/60">
              No credit card. No app to download. Just relief.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}


/* ---------------- Feature / difference bits ---------------- */

function FeatureCol({ icon, title, body }) {
  return (
    <div className="text-center flex flex-col items-center">
      <div className="w-16 h-16 rounded-full bg-[#EFE6D5] flex items-center justify-center mb-6">{icon}</div>
      <div
        className="text-[19px] leading-[1.25] text-[#2A2823]"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {title}
      </div>
      <p className="mt-4 text-[13px] text-[#2A2823]/70 max-w-[240px] leading-relaxed">{body}</p>
    </div>
  );
}

function DifferenceCol({ title }) {
  return (
    <div className="text-center flex flex-col items-center px-4 py-8 md:py-4">
      <SmallSprig className="mb-4" />
      <div
        className="text-[15px] sm:text-[17px] leading-[1.35] text-[#2A2823]"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {title}
      </div>
    </div>
  );
}

/* ---------------- Icons + sprigs ---------------- */

function BowlIcon() {
  return (
    <svg viewBox="0 0 48 48" width="34" height="34" fill="none" stroke="#3E4A37" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10c0 1 1 2 0 4M22 8c0 1 1 2 0 4M26 10c0 1 1 2 0 4M30 8c0 1 1 2 0 4" />
      <path d="M8 22h32c0 8-6 14-16 14S8 30 8 22z" />
      <path d="M12 22c0-3 3-6 12-6s12 3 12 6" />
      <path d="M18 30l1 3M24 31v3M30 30l-1 3" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 48 48" width="34" height="34" fill="none" stroke="#3E4A37" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="10" width="24" height="30" rx="2" />
      <rect x="18" y="7" width="12" height="6" rx="1.5" fill="#3E4A37" opacity="0.9" />
      <path d="M17 20l2 2 3-3M17 27l2 2 3-3M17 34l2 2 3-3" />
      <path d="M26 22h6M26 29h6M26 36h6" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 48 48" width="34" height="34" fill="none" stroke="#3E4A37" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="18" r="4" />
      <circle cx="14" cy="20" r="3" />
      <circle cx="34" cy="20" r="3" />
      <path d="M16 34c1-4 4-6 8-6s7 2 8 6" />
      <path d="M6 32c1-3 3-5 6-5M42 32c-1-3-3-5-6-5" />
      <path d="M22 40l2-2 2 2" />
      <path d="M20 40c1-1 2-1.5 4-1.5s3 .5 4 1.5" strokeDasharray="0" />
    </svg>
  );
}

function SmallSprig({ className = "" }) {
  return (
    <svg viewBox="0 0 40 40" width="28" height="28" className={className} fill="none" stroke="#3E4A37" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 34c2-10 8-18 16-24" />
      <path d="M26 22c-3-1-5-3-5-6" fill="#7c8e69" fillOpacity="0.35" />
      <path d="M31 16c-3-1-5-3-5-6" fill="#7c8e69" fillOpacity="0.35" />
      <path d="M22 28c-3-1-5-3-5-6" fill="#7c8e69" fillOpacity="0.35" />
    </svg>
  );
}

function BotanicalSVG({ className = "" }) {
  return (
    <svg viewBox="0 0 200 260" className={className} fill="none">
      <g stroke="#7c8e69" strokeWidth="1.4" strokeLinecap="round" fill="none">
        <path d="M40 250c30-60 60-120 120-200" />
      </g>
      <g fill="#8a9c74" opacity="0.6">
        <ellipse cx="70" cy="200" rx="12" ry="5" transform="rotate(-30 70 200)" />
        <ellipse cx="95" cy="160" rx="14" ry="6" transform="rotate(-30 95 160)" />
        <ellipse cx="120" cy="120" rx="15" ry="6" transform="rotate(-30 120 120)" />
        <ellipse cx="145" cy="80" rx="12" ry="5" transform="rotate(-30 145 80)" />
        <ellipse cx="55" cy="220" rx="10" ry="4" transform="rotate(30 55 220)" />
        <ellipse cx="85" cy="180" rx="12" ry="5" transform="rotate(30 85 180)" />
        <ellipse cx="110" cy="140" rx="13" ry="5" transform="rotate(30 110 140)" />
      </g>
    </svg>
  );
}
